import React, { useState, useEffect } from "react";
import {
  Grid,
  Card,
  Text,
  Button,
  Skeleton,
  Group,
  Select,
  Pagination,
  Stack,
  Center,
  Paper,
  Alert,
} from "@mantine/core";
import { useNavigate } from "react-router-dom";
import { useCampaign } from "../../context/CampaignContext";
import { formatDateTime } from "../../utils/timeUtils";
import { useSalon } from "../../context/SalonContext";
import usePermissions from "../../hooks/usePermissions";

const PAGE_SIZE_OPTIONS = [20, 40, 60];

function truncate(str, n) {
  // description (and other campaign text) is nullable — guard so a campaign with
  // no description doesn't crash the whole list.
  if (!str) return "";
  return str.length > n ? str.slice(0, n - 1) + "…" : str;
}

// `embedded` = rendered inside the Marketing page tab shell, which already
// provides the page padding, a "Marketing" page title + "Campaigns" tab, and
// the New Campaign action in its header. In that mode we drop the internal
// padding, heading, and the top-of-list New Campaign button.
function MarketingOverview({ embedded = false }) {
  const navigate = useNavigate();
  const { campaigns, campaignLoading, campaignErrors, fetchCampaigns } =
    useCampaign();
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(PAGE_SIZE_OPTIONS[0]);
  const { businessInfo } = useSalon();
  const perms = usePermissions();
  const marketingPerms = perms?.marketing;
  useEffect(() => {
    fetchCampaigns();
  }, [fetchCampaigns]);

  // Pagination logic
  const totalPages = Math.ceil(campaigns.length / pageSize);
  const pagedCampaigns = campaigns.slice(
    (page - 1) * pageSize,
    page * pageSize,
  );

  // Handler for viewing a campaign
  const handleViewCampaign = (uuid) => {
    navigate(`/admin/marketing/${uuid}`);
  };

  return (
    <Stack
      p={embedded ? 0 : "md"}
      gap="md"
      style={{ minHeight: embedded ? undefined : "100dvh" }}
    >
      {!embedded && (
        <Group justify="space-between" align="center">
          <Text size="xl" fw={700}>
            Marketing Campaigns
          </Text>
          {marketingPerms?.canCreate !== false && (
            <Button
              color="var(--brand-teal)"
              onClick={() => navigate("/admin/marketing/new")}
            >
              New Campaign
            </Button>
          )}
        </Group>
      )}
      {campaignLoading.list ? (
        <Grid gutter="md">
          {Array.from({ length: pageSize }).map((_, i) => (
            <Grid.Col key={i} span={{ base: 12, sm: 6, md: 4, lg: 3 }}>
              <Skeleton height={120} radius="md" />
            </Grid.Col>
          ))}
        </Grid>
      ) : campaignErrors.list ? (
        <Alert color="red" title="Error">
          {campaignErrors.list}
        </Alert>
      ) : campaigns.length === 0 ? (
        <Center h={300}>
          <Paper p="xl" radius="md" withBorder shadow="sm">
            <Stack gap="xs" align="center">
              <Text size="lg" fw={500}>
                No Campaigns Yet
              </Text>
              <Text color="dimmed" align="center">
                You haven't created any marketing campaigns yet. Start your
                first campaign to engage your clients and grow your business!
              </Text>
              {marketingPerms?.canCreate !== false && (
                <Button
                  color="var(--brand-teal)"
                  mt="md"
                  onClick={() => navigate("/admin/marketing/new")}
                >
                  Create Your First Campaign
                </Button>
              )}
            </Stack>
          </Paper>
        </Center>
      ) : (
        <>
          <Grid gutter="md" style={{ flexGrow: 1 }}>
            {pagedCampaigns.map((campaign) => (
              <Grid.Col
                key={campaign.uuid}
                span={{ base: 12, sm: 6, md: 4, lg: 3 }}
              >
                <Card
                  shadow="sm"
                  padding="lg"
                  radius="md"
                  withBorder
                  style={{ minHeight: 120, cursor: "pointer" }}
                  onClick={() => handleViewCampaign(campaign.uuid)}
                >
                  <Text size="md" fw={500}>
                    {campaign.name}
                  </Text>
                  <Text size="sm" c="dimmed" mt={4} lineClamp={2}>
                    {truncate(campaign.description, 80)}
                  </Text>
                  <Text size="xs" c="gray" mt={8}>
                    {formatDateTime(
                      campaign.start_date,
                      businessInfo?.timezone,
                      "MMM D, YYYY h:mm A",
                    )}
                  </Text>
                  <Button
                    variant="light"
                    color="var(--brand-teal)"
                    fullWidth
                    mt="md"
                    onClick={(e) => {
                      e.stopPropagation();
                      handleViewCampaign(campaign.uuid);
                    }}
                  >
                    View
                  </Button>
                </Card>
              </Grid.Col>
            ))}
          </Grid>
          <Group justify="space-between" mt="md" align="center">
            <Group align="center" gap={8}>
              <Text size="sm" c="dimmed">
                Records per page:
              </Text>
              <Select
                value={pageSize.toString()}
                onChange={(v) => {
                  setPageSize(Number(v));
                  setPage(1);
                }}
                data={PAGE_SIZE_OPTIONS.map((n) => ({
                  value: n.toString(),
                  label: n.toString(),
                }))}
                style={{ width: 80 }}
              />
            </Group>
            <Pagination page={page} onChange={setPage} total={totalPages} />
          </Group>
        </>
      )}
    </Stack>
  );
}

export default MarketingOverview;
